import { useState, useEffect } from "react";
import VISA from "../../../assets/images/visa-payment.png";
import MasterCard from "../../../assets/images/mastercard-payment.png";
import PayPal from "../../../assets/images/paypal-payment.png";
import Amex from "../../../assets/images/amex-payment.png";
import styles from "../checkout.module.scss";
import { usePromotion } from "../../../hooks/usePromotion";

const OrderSummaryForm = (props) => {
  const { cartItems, updatePaymentType, onPriceUpdate, onCartItemsUpdate, onProcessFreeCheckout } =
    props;

  const [totalAmount, setTotalAmount] = useState(0);
  const [paymentType, setPaymentType] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [localCartItems, setLocalCartItems] = useState(cartItems);

  const promoTGSingle = usePromotion("151");
  const promoTGMulti = usePromotion("170");
  const promoObesIDSingle = usePromotion("2151");
  const promoObesIDMulti = usePromotion("2170");
  const promoDRTCSingle = usePromotion("3151");
  const promoDRTCMulti = usePromotion("3170");

  const promoMap = {
    151: promoTGSingle,
    170: promoTGMulti,
    2151: promoObesIDSingle,
    2170: promoObesIDMulti,
    3151: promoDRTCSingle,
    3170: promoDRTCMulti,
  };

  useEffect(() => {
    setLocalCartItems(cartItems);
  }, [cartItems]);

  useEffect(() => {
    if (localCartItems && localCartItems.length > 0) {
      const grandTotal = localCartItems.reduce((acc, item) => {
        const unitPrice =
          item.hasappliedcoupon && item.updatedPrice !== undefined
            ? item.updatedPrice
            : getDisplayPrice(item);

        return acc + unitPrice * (item.quantity || 1);
      }, 0);

      setTotalAmount(grandTotal);
      onPriceUpdate(grandTotal);
    }
  }, [
    localCartItems,
    onPriceUpdate,
    promoTGSingle,
    promoTGMulti,
    promoObesIDSingle,
    promoObesIDMulti,
    promoDRTCSingle,
    promoDRTCMulti,
  ]);

  const handlePaymentType = (event) => {
    const selectedPaymentType = event.target.value;
    setPaymentType(selectedPaymentType);
    if (selectedPaymentType !== null) {
      updatePaymentType(selectedPaymentType);
      props.nextSteps();
    }
  };

  const isComplimentary = totalAmount === 0;

  const handlePromoCode = async () => {
    if (!promoCode) return alert("Please enter a promo code");
    if (isApplyingCoupon) return;
    setIsApplyingCoupon(true);

    try {
      const response = await fetch("/api/CouponApi", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "validate",
          couponCode: promoCode.trim(),
          // Pass current local state so backend knows what's already discounted
          cartItems: localCartItems.map((item) => ({
            subscriptionid: item.subscriptionid,
            price: getDisplayPrice(item),
            quantity: item.quantity,
            ordertype: item.ordertype,
            hasappliedcoupon: !!item.hasappliedcoupon,
          })),
        }),
      });

      const data = await response.json();

      if (data.valid) {
        const finalUpdatedItems = localCartItems.map((item) => {
          // If this is the specific item the backend just validated
          if (
            String(item.subscriptionid) === data.appliedToSubscriptionId &&
            !item.hasappliedcoupon
          ) {
            return {
              ...item,
              ...data.update,
              hasappliedcoupon: true,
              tg_couponreferrercode: data.couponCode,
            };
          }
          return item;
        });

        setLocalCartItems(finalUpdatedItems);
        if (onCartItemsUpdate) onCartItemsUpdate(finalUpdatedItems);
        setPromoCode("");
        alert("Coupon applied successfully!");
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsApplyingCoupon(false);
    }
  };
  const getProductName = (item) => {
    const pubName = ["2151", "2170"].includes(item.subscriptionid)
      ? "ObesiD"
      : ["3151", "3170"].includes(item.subscriptionid)
        ? "Don't Rush to Crush"
        : "Therapeutic Guidelines";
    const subType = ["151", "2151", "3151"].includes(item.subscriptionid)
      ? "SingleUser"
      : "MultiUser";
    const orderType =
      item.ordertype !== "New" ? ` - ${item.ordertype?.toLowerCase()} license(s)` : "";
    return `${pubName} ${subType} Subscription${orderType}`;
  };

  const getDisplayPrice = (item) => {
    if (item.updatedPrice !== undefined) {
      return item.updatedPrice;
    }

    const promo = promoMap[String(item.subscriptionid)];
    if (promo) {
      return parseFloat(promo.price);
    }

    const apiPrice = ["151", "2151", "3151"].includes(item.subscriptionid)
      ? item.individualprice
      : item.multipleprice;

    //  If API price is valid (not 0 or null), return it
    if (apiPrice && apiPrice > 0) return apiPrice;

    //  Fallback to Environment Variables if API returns 0 or null
    switch (item.subscriptionid) {
      case "151": // TG Single
        return parseFloat(import.meta.env.VITE_TG_SINGLE_USER_PRICE || 0);
      case "2151": // ObesiD Single
        return parseFloat(import.meta.env.VITE_OB_SINGLE_USER_PRICE || 0);
      case "3151": // DRTÇ Single
        return parseFloat(import.meta.env.VITE_DRTC_SINGLE_USER_PRICE || 0);
      default:
        return 0;
    }
  };

  return (
    <div className={styles.orderSummaryForm}>
      <h4 className="section-heading">Shopping cart</h4>
      <div className={styles.tableResponsive}>
        <table className={styles.customTable}>
          <thead>
            <tr className={styles.tealHeader}>
              <th style={{ width: "60%" }}>Product</th>
              <th style={{ width: "10%" }}>Quantity</th>
              <th style={{ width: "30%" }}>Price (AUD, incl GST)</th>
            </tr>
          </thead>
          <tbody>
            {localCartItems.map((item) => (
              <tr key={item.cartsid}>
                <td data-title="Product">
                  <div className={styles.productName}>{getProductName(item)}</div>
                  {item.hasappliedcoupon && (
                    <div className={styles.promoLabel}>{item.promoLabel}</div>
                  )}
                </td>

                <td data-title="Quantity" className={styles.quantityCell}>
                  <span>{item.quantity}</span>
                </td>
                <td data-title="Price">
                  {item.hasappliedcoupon ? (
                    <div>
                      <div className={styles.originalPrice}>
                        ${(item.originalPrice * item.quantity).toFixed(2)}
                      </div>
                      <div className={styles.discountedPrice}>
                        ${(item.updatedPrice * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  ) : promoMap[String(item.subscriptionid)] ? (
                    <div>
                      <div className={styles.originalPrice}>
                        $
                        {(
                          promoMap[String(item.subscriptionid)].originalPrice * item.quantity
                        ).toFixed(2)}
                      </div>
                      <div className={styles.discountedPrice}>
                        ${(getDisplayPrice(item) * item.quantity).toFixed(2)}
                      </div>
                      <div className={styles.promoLabel}>
                        {promoMap[String(item.subscriptionid)].label}
                      </div>
                    </div>
                  ) : (
                    <span>${(getDisplayPrice(item) * item.quantity).toFixed(2)}</span>
                  )}
                </td>
              </tr>
            ))}

            {/* Subtotal Row */}
            <tr className={styles.tealRow}>
              <td colSpan="2" className={styles.textRight}>
                Subtotal:
              </td>
              <td>${totalAmount?.toFixed(2)}</td>
            </tr>

            {/* Promo Code Row */}
            <tr>
              <td colSpan="3" className={styles.promoCell}>
                <label htmlFor="promoCodeValue" className={styles.srOnly}>
                  Promo code
                </label>
                <div className={styles.promoWrapper}>
                  <input
                    type="text"
                    className={styles.promoInput}
                    id="promoCodeValue"
                    name="promoCodeValue"
                    placeholder="Promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                  />
                  <button
                    className={styles.applyBtn}
                    onClick={handlePromoCode}
                    disabled={isApplyingCoupon || paymentType !== null}
                  >
                    {isApplyingCoupon ? "Applying..." : "Apply"}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr className={styles.tealRow}>
              <td className={styles.textRight} colSpan="2">
                Total :
              </td>
              <td id="totalPriceId">${totalAmount?.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>

        {isComplimentary && (
          <div className={styles.complimentaryMessage}>
            <h4>Complimentary Access</h4>
            <p>Your order is complimentary. No payment is required.</p>
            <button className={styles.btnPrimary} onClick={onProcessFreeCheckout}>
              Click here to finalise your order
            </button>
          </div>
        )}

        {!isComplimentary && (
          <>
            <h4 id="paymentBox" className={styles.paymentHeading}>
              Select payment method:
            </h4>
            <div id="paymentImg">
              <div className={styles.paymentOptions}>
                <div className={styles.paymentRow}>
                  <div className={styles.radioWrapper}>
                    <input
                      type="radio"
                      name="paymentChoice"
                      id="cc_choice"
                      value="cc"
                      onChange={handlePaymentType}
                    />
                  </div>
                  <label htmlFor="cc_choice" className={styles.paymentLabel}>
                    <img src={VISA} alt="Visa" />
                    <img src={MasterCard} alt="MasterCard" />
                    <img src={Amex} alt="Amex" />
                  </label>
                </div>

                <div className={styles.paymentRow}>
                  <div className={styles.radioWrapper}>
                    <input
                      type="radio"
                      name="paymentChoice"
                      id="paypal_choice"
                      value="paypal"
                      onChange={handlePaymentType}
                    />
                  </div>
                  <label htmlFor="paypal_choice" className={styles.paymentLabel}>
                    <img src={PayPal} alt="PayPal" />
                  </label>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderSummaryForm;
